import { assertUncomputedFromBase, fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { constructInventoryDefinitionReferences } from '../janus-engine/compile/from-base-inventory-references';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { isRuneStatusLayer } from '../rune-icon/rune-icon-engine';
import { itemsCol } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { definitionColumns, definitionPlCell, definitionRow } from './definition-cells';
import { isBaseDefinitionListSchema } from './types';
import type { BaseDefinitionListResponse, CatalogTableResponse } from './types';

/** The reference-nesting stage alone needs no shared/compiler metadata. Only
 * status-bearing layers enter it; every other cell keeps reading the saved root. */
export function savedDefinitionRuneView(root: FromBaseRow, catalog: Record<string, unknown>): FromBaseRow {
    const layers = fromBaseRecord(root.item_layer_types) ? root.item_layer_types : {};
    const statusLayers = Object.fromEntries(Object.entries(layers).filter(([key]) => isRuneStatusLayer(key)));
    const lookup = (id: string): FromBaseRow | null => {
        const row = catalog[id] ?? catalog[id.toUpperCase()];
        return fromBaseRecord(row) ? row : null;
    };
    const nested = constructInventoryDefinitionReferences({ items: root.items, item_layer_types: statusLayers }, {
        layerCatalog: Object.fromEntries(Object.keys(statusLayers).map(key => [key,
            { references_item_type: 'status_effect', full_nest: true, is_multiple: true }])),
        shellRegistries: { status_effect: null }, sizeCatalog: [],
    }, lookup, { fullDefinition: lookup });
    const runeCatalog: Record<string, unknown> = {};
    const visit = (value: unknown): void => {
        if (typeof value === 'string') {
            const ref = lookup(value);
            if (ref) runeCatalog[value.toUpperCase()] = { item_name: decodeHtmlEntitiesForPlainText(itemsCol(ref, 'item_name')),
                item_type: itemsCol(ref, 'item_type') };
        } else if (value && typeof value === 'object') Object.values(value).forEach(visit);
    };
    visit(layers);
    return { ...root, catalog: runeCatalog, item_layer_types: { ...layers, ...nested.item_layer_types as FromBaseRow } };
}

/** Saved rows paint independently; the shared core store upgrades visible runes. */
export async function adoptBaseDefinitionList(data: BaseDefinitionListResponse,
    progress?: (done: number, total: number) => void): Promise<CatalogTableResponse> {
    if (!isBaseDefinitionListSchema(data.schema) || !['spells', 'abilities'].includes(data.family)
        || !Array.isArray(data.definition_ids) || data.total !== data.definition_ids.length)
        throw new Error('catalog-base-definition-schema');
    const output: CatalogTableResponse = { columns: definitionColumns(data.family, data.item_type), rows: [], total: data.total,
        pl_persist: data.pl_persist, definition_schema: data.schema, definition_refusals: [] };
    if (!data.total) return output;
    assertUncomputedFromBase(data.base);
    const catalog = fromBaseRecord(data.base.catalog) ? data.base.catalog : {};
    let yieldDeadline = performance.now() + 8;
    for (const id of data.definition_ids) {
        const saved = catalog[id] ?? catalog[id.toUpperCase()];
        if (!fromBaseRecord(saved)) {
            const reason = 'unported:root-definition:' + id;
            output.rows.push({ item_id: id, cells: { name: { sort: id, text: (id + ' ' + reason).toLowerCase(),
                segments: [{ kind: 'text', value: id }, { kind: 'text', value: ' ' + reason, cls: 'text-warning' }] } } });
            output.definition_refusals!.push({ item_id: id, name: id, reasons: [reason] });
        } else {
            let runeView = saved;
            try { runeView = savedDefinitionRuneView(saved, catalog); } catch { /* Plain saved rune until the core upgrade. */ }
            const row = definitionRow(saved, data.family, catalog, runeView);
            const price = data.stored_pl?.[id] ?? data.stored_pl?.[id.toUpperCase()];
            const pl = typeof price === 'number' && Number.isFinite(price) ? price : undefined;
            row.cells.pl = definitionPlCell(pl, id);
            row.filters!.pl = pl === undefined ? '' : String(pl);
            output.rows.push(row);
        }
        if (output.rows.length % 8 === 0) {
            progress?.(output.rows.length, data.total);
            // Avoid the nested-timer minimum for each eight inexpensive definitions.
            if (performance.now() >= yieldDeadline) {
                await new Promise<void>(resolve => setTimeout(resolve, 0));
                yieldDeadline = performance.now() + 8;
            }
        }
    }
    progress?.(data.total, data.total);
    return output;
}
